import { useMemo } from 'react'

import { UserRoutes } from '~config/routes'
import { useUserContext } from '~config/user-context'
import { useClinic } from '~contexts/clinic-context'
import {
    AdminPermissionFeature,
    FilterOp,
    QueryInput,
    useQueryClientsPatientsQuery,
    useQueryCliniciansQuery,
    useQueryCustomerSegmentsQuery,
    useQueryNotificationTemplatesQuery,
    useQueryPatientsOwnersQuery,
    useQueryRoomsQuery,
    useQueryServicesQuery,
} from '~graphql/generated/graphql'
import {
    getClientFullName,
    getExisting,
    getHasPermissions,
} from '~utils/helpers'
import { useDebounce } from '~utils/hooks/use-debounce'
import { processSearchQuery } from '~utils/hooks/use-search-clients'
import {
    eqBoolFilter,
    eqStringFilter,
    iLikeStringFilter,
    isNullFilter,
} from '~utils/query-helpers'
import { formatPhoneReadable } from '~utils/phone'
import { searchableStaticItems } from './static-search-items'
import { searchStaticItems } from './static-search'
import {
    UniversalSearchEntityResult,
    UniversalSearchResult,
    UniversalSearchResultGroup,
    UniversalSearchStaticItem,
} from './types'

const ENTITY_RESULT_LIMIT = 5
const STATIC_RESULT_LIMIT = 12

export function useUniversalSearch(query: string) {
    const { clinic, permissions } = useClinic()
    const { isAdmin } = useUserContext()
    const debouncedQuery = useDebounce(query, 500)

    const trimmedQuery = query.trim()
    const trimmedDebouncedQuery = debouncedQuery.trim()
    const hasSearchableEntityText = /[a-zA-Z0-9]/.test(trimmedDebouncedQuery)

    const visibleStaticItems = useMemo(
        () =>
            searchableStaticItems.filter(item =>
                isStaticItemVisible(item, { clinic, permissions, isAdmin }),
            ),
        [clinic, isAdmin, permissions],
    )

    const staticResults = useMemo(
        () =>
            searchStaticItems(visibleStaticItems, trimmedQuery).slice(
                0,
                STATIC_RESULT_LIMIT,
            ),
        [trimmedQuery, visibleStaticItems],
    )

    const canSearchClientsAndPatients = Boolean(clinic?.id)
    const canSearchServices = getHasPermissions(
        permissions,
        AdminPermissionFeature.Services,
        isAdmin,
    )
    const canSearchClinicians =
        Boolean(clinic?.settings?.does_use_clinicians_to_schedule) &&
        getHasPermissions(
            permissions,
            AdminPermissionFeature.Clinicians,
            isAdmin,
        )
    const canSearchRooms =
        Boolean(clinic?.settings?.does_use_rooms_to_schedule) &&
        getHasPermissions(permissions, AdminPermissionFeature.Rooms, isAdmin)
    const canSearchNotifications = getHasPermissions(
        permissions,
        AdminPermissionFeature.Notifications,
        isAdmin,
    )
    const canSearchAudiences = getHasPermissions(
        permissions,
        AdminPermissionFeature.ClientSegments,
        isAdmin,
    )
    const shouldSearchEntities = Boolean(
        clinic?.id && trimmedDebouncedQuery && hasSearchableEntityText,
    )

    const ftsSearchValue = useMemo(
        () => processSearchQuery(trimmedDebouncedQuery),
        [trimmedDebouncedQuery],
    )

    const clientWhere = useMemo<QueryInput[]>(
        () => [
            ...(clinic?.tmp_feature_flags?.only_use_this_location && clinic.id
                ? [eqStringFilter('client_clinic', clinic.id)]
                : []),
            {
                column: 'vector_search_field',
                filterOp: FilterOp.Fts,
                value: {
                    string: ftsSearchValue,
                },
            },
            isNullFilter('client_pms_id', true),
            eqBoolFilter('client_is_active', true),
        ],
        [
            clinic?.id,
            clinic?.tmp_feature_flags?.only_use_this_location,
            ftsSearchValue,
        ],
    )

    const patientWhere = useMemo<QueryInput[]>(
        () => [
            {
                column: 'vector_search_field',
                filterOp: FilterOp.Fts,
                value: {
                    string: ftsSearchValue,
                },
            },
            isNullFilter('patient_pms_id', true),
            eqBoolFilter('patient_is_active', true),
            eqBoolFilter('patient_is_deceased', false),
        ],
        [ftsSearchValue],
    )

    const servicesWhere = useMemo<QueryInput[]>(
        () => [
            eqStringFilter('clinic', clinic?.id),
            iLikeStringFilter('name', trimmedDebouncedQuery),
            eqBoolFilter('is_active', true),
        ],
        [clinic?.id, trimmedDebouncedQuery],
    )

    const cliniciansWhere = useMemo<QueryInput[]>(
        () => [
            eqStringFilter('clinic', clinic?.id),
            iLikeStringFilter('name', trimmedDebouncedQuery),
            eqBoolFilter('is_active', true),
        ],
        [clinic?.id, trimmedDebouncedQuery],
    )

    const roomsWhere = useMemo<QueryInput[]>(
        () => [
            eqStringFilter('clinic', clinic?.id),
            iLikeStringFilter('name', trimmedDebouncedQuery),
            eqBoolFilter('is_active', true),
        ],
        [clinic?.id, trimmedDebouncedQuery],
    )

    const notificationsWhere = useMemo<QueryInput[]>(
        () => [
            eqStringFilter('clinic', clinic?.id),
            iLikeStringFilter('name', trimmedDebouncedQuery),
            eqBoolFilter('is_archived', false),
        ],
        [clinic?.id, trimmedDebouncedQuery],
    )

    const audiencesWhere = useMemo<QueryInput[]>(
        () => [
            eqStringFilter('clinic', clinic?.id),
            iLikeStringFilter('name', trimmedDebouncedQuery),
            eqBoolFilter('is_archived', false),
        ],
        [clinic?.id, trimmedDebouncedQuery],
    )

    const clientsQuery = useQueryClientsPatientsQuery({
        variables: {
            clinicId: clinic?.id || '',
            where: clientWhere,
            page: 0,
            pageSize: ENTITY_RESULT_LIMIT,
        },
        skip: !shouldSearchEntities || !canSearchClientsAndPatients,
    })

    const patientsQuery = useQueryPatientsOwnersQuery({
        variables: {
            clinicId: clinic?.id || '',
            where: patientWhere,
            page: 0,
            pageSize: ENTITY_RESULT_LIMIT,
        },
        skip: !shouldSearchEntities || !canSearchClientsAndPatients,
    })

    const servicesQuery = useQueryServicesQuery({
        variables: {
            where: servicesWhere,
            page: 0,
            pageSize: ENTITY_RESULT_LIMIT,
        },
        skip: !shouldSearchEntities || !canSearchServices,
    })

    const cliniciansQuery = useQueryCliniciansQuery({
        variables: {
            where: cliniciansWhere,
            page: 0,
            pageSize: ENTITY_RESULT_LIMIT,
        },
        skip: !shouldSearchEntities || !canSearchClinicians,
    })

    const roomsQuery = useQueryRoomsQuery({
        variables: {
            where: roomsWhere,
            page: 0,
            pageSize: ENTITY_RESULT_LIMIT,
        },
        skip: !shouldSearchEntities || !canSearchRooms,
    })

    const notificationsQuery = useQueryNotificationTemplatesQuery({
        variables: {
            where: notificationsWhere,
            page: 0,
            pageSize: ENTITY_RESULT_LIMIT,
        },
        skip: !shouldSearchEntities || !canSearchNotifications,
    })

    const audiencesQuery = useQueryCustomerSegmentsQuery({
        variables: {
            where: audiencesWhere,
            page: 0,
            pageSize: ENTITY_RESULT_LIMIT,
        },
        skip: !shouldSearchEntities || !canSearchAudiences,
    })

    const clientResults = useMemo<UniversalSearchEntityResult[]>(
        () =>
            getExisting(
                clientsQuery.data?.queryClientsPatients.results.map(
                    result => result.client,
                ) || [],
            ).map(client => ({
                id: `client.${client.id}`,
                kind: 'client',
                title: getClientFullName(client) || 'Client',
                subtitle: [
                    formatPhoneReadable(
                        client.phone,
                        client.address?.country,
                    ) || client.phone,
                    client.email,
                ]
                    .filter(Boolean)
                    .join(' · '),
                route: UserRoutes.ClientDetails(client.id),
                client,
            })),
        [clientsQuery.data?.queryClientsPatients.results],
    )

    const patientResults = useMemo<UniversalSearchEntityResult[]>(
        () =>
            getExisting(
                patientsQuery.data?.queryPatientsOwners.patients || [],
            ).map(patient => ({
                id: `patient.${patient.id}`,
                kind: 'patient',
                title: patient.name || 'Patient',
                subtitle: [
                    patient.owner
                        ? getClientFullName(patient.owner)
                        : undefined,
                    patient.clinic_species?.description,
                ]
                    .filter(Boolean)
                    .join(' · '),
                route: UserRoutes.PatientDetails(patient.id),
                patient,
            })),
        [patientsQuery.data?.queryPatientsOwners.patients],
    )

    const serviceResults = useMemo<UniversalSearchEntityResult[]>(
        () =>
            getExisting(servicesQuery.data?.queryServices || []).map(
                service => ({
                    id: `service.${service.id}`,
                    kind: 'service',
                    title: service.name || 'Service',
                    subtitle: service.description || 'Booking service',
                    route: UserRoutes.EditService(service.id),
                    service,
                }),
            ),
        [servicesQuery.data?.queryServices],
    )

    const clinicianResults = useMemo<UniversalSearchEntityResult[]>(
        () =>
            getExisting(cliniciansQuery.data?.queryClinicians || []).map(
                clinician => ({
                    id: `clinician.${clinician.id}`,
                    kind: 'clinician',
                    title: clinician.display_name || clinician.name,
                    subtitle: clinician.is_active
                        ? 'Active clinician'
                        : 'Inactive clinician',
                    route: UserRoutes.Clinicians,
                    clinician,
                }),
            ),
        [cliniciansQuery.data?.queryClinicians],
    )

    const roomResults = useMemo<UniversalSearchEntityResult[]>(
        () =>
            getExisting(roomsQuery.data?.queryRooms || []).map(room => ({
                id: `room.${room.id}`,
                kind: 'room',
                title: room.display_name || room.name,
                subtitle: room.is_active ? 'Active room' : 'Inactive room',
                route: UserRoutes.Rooms,
                room,
            })),
        [roomsQuery.data?.queryRooms],
    )

    const notificationResults = useMemo<UniversalSearchEntityResult[]>(
        () =>
            getExisting(
                notificationsQuery.data?.queryNotificationTemplates || [],
            ).map(notification => ({
                id: `notification.${notification.id}`,
                kind: 'notification',
                title: notification.name,
                subtitle: notification.is_active
                    ? 'Active notification'
                    : 'Inactive notification',
                route: UserRoutes.EditNotification(notification.id),
                notification,
            })),
        [notificationsQuery.data?.queryNotificationTemplates],
    )

    const audienceResults = useMemo<UniversalSearchEntityResult[]>(
        () =>
            getExisting(audiencesQuery.data?.queryCustomerSegments || []).map(
                audience => ({
                    id: `audience.${audience.id}`,
                    kind: 'audience',
                    title: audience.name,
                    subtitle: audience.is_archived
                        ? 'Archived audience'
                        : 'Audience',
                    route: UserRoutes.AudienceDetails(audience.id),
                    audience,
                }),
            ),
        [audiencesQuery.data?.queryCustomerSegments],
    )

    const groups = useMemo<UniversalSearchResultGroup[]>(
        () =>
            [
                makeGroup(
                    'settings',
                    'Settings',
                    staticResults.filter(result => result.kind === 'setting'),
                ),
                makeGroup(
                    'pages',
                    'Pages',
                    staticResults.filter(result => result.kind === 'page'),
                ),
                makeGroup(
                    'clients',
                    'Clients',
                    clientResults,
                    clientsQuery.loading,
                ),
                makeGroup(
                    'patients',
                    'Patients',
                    patientResults,
                    patientsQuery.loading,
                ),
                makeGroup(
                    'services',
                    'Services',
                    serviceResults,
                    servicesQuery.loading,
                ),
                makeGroup(
                    'clinicians',
                    'Clinicians',
                    clinicianResults,
                    cliniciansQuery.loading,
                ),
                makeGroup('rooms', 'Rooms', roomResults, roomsQuery.loading),
                makeGroup(
                    'notifications',
                    'Notifications',
                    notificationResults,
                    notificationsQuery.loading,
                ),
                makeGroup(
                    'audiences',
                    'Audiences',
                    audienceResults,
                    audiencesQuery.loading,
                ),
            ].filter(group => group.results.length > 0 || group.isLoading),
        [
            audienceResults,
            audiencesQuery.loading,
            clinicianResults,
            cliniciansQuery.loading,
            clientResults,
            clientsQuery.loading,
            notificationResults,
            notificationsQuery.loading,
            patientResults,
            patientsQuery.loading,
            serviceResults,
            servicesQuery.loading,
            roomResults,
            roomsQuery.loading,
            staticResults,
        ],
    )

    const loading =
        clientsQuery.loading ||
        patientsQuery.loading ||
        servicesQuery.loading ||
        cliniciansQuery.loading ||
        roomsQuery.loading ||
        notificationsQuery.loading ||
        audiencesQuery.loading

    return {
        groups,
        loading,
        hasQuery: Boolean(trimmedQuery),
    }
}

function isStaticItemVisible(
    item: UniversalSearchStaticItem,
    context: {
        clinic: ReturnType<typeof useClinic>['clinic']
        permissions: ReturnType<typeof useClinic>['permissions']
        isAdmin?: boolean
    },
): boolean {
    const hasRequiredPermission = item.requiredPermission
        ? getHasPermissions(
              context.permissions,
              item.requiredPermission,
              context.isAdmin,
          )
        : true
    if (!hasRequiredPermission) return false
    return item.isVisible
        ? item.isVisible({
              clinic: context.clinic,
              isAdmin: context.isAdmin,
          })
        : true
}

function makeGroup(
    id: string,
    label: string,
    results: UniversalSearchResult[],
    isLoading?: boolean,
): UniversalSearchResultGroup {
    return {
        id,
        label,
        results,
        isLoading,
    }
}
